import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FormContainer from '../../components/ui/FormContainer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AvailabilityToggle from './components/AvailabilityToggle';
import { generateId } from '../../utils/generateId';
import { loadFormData, saveProgress, getUserEmail } from '../../utils/onboardingApi';

const InvestorRelationsContact = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    jobTitle: '',
    directEmail: '',
    directPhone: '',
    linkedinUrl: '',
    timezone: '',
    importFromPreviousDeal: '',
    weeklyAvailability: {
      monday: { available: true, from: '09:00', until: '17:00' },
      tuesday: { available: true, from: '09:00', until: '17:00' },
      wednesday: { available: true, from: '09:00', until: '17:00' },
      thursday: { available: true, from: '09:00', until: '17:00' },
      friday: { available: true, from: '09:00', until: '17:00' },
      saturday: { available: true, from: '09:00', until: '17:00' },
      sunday: { available: true, from: '09:00', until: '17:00' }
    }
  });

  const [selectedDealForImport, setSelectedDealForImport] = useState('');
  const [dealOptions, setDealOptions] = useState([]);
  const [currentInvestorRelationsId, setCurrentInvestorRelationsId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);


  // Load saved form data from API on component mount
  useEffect(() => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      console.warn('No email found. User may need to access via email link.');
      setIsLoading(false);
      return;
    }

    // Try to load from API
    loadFormData('ir-contact')
      .then(data => {
        if (Object.keys(data).length > 0) {
          setFormData(prev => ({
            ...prev,
            fullName: data?.fullName || prev?.fullName,
            jobTitle: data?.jobTitle || prev?.jobTitle,
            directEmail: data?.directEmail || prev?.directEmail,
            directPhone: data?.directPhone || prev?.directPhone,
            linkedinUrl: data?.linkedinUrl || prev?.linkedinUrl,
            timezone: data?.timezone || prev?.timezone,
            weeklyAvailability: data?.weeklyAvailability || prev?.weeklyAvailability
          }));
        } else {
          // Fallback to localStorage for legacy data
          const savedData = localStorage.getItem('investorRelationsDraft');
          if (savedData) {
            try {
              const parsed = JSON.parse(savedData);
              setFormData(prev => ({...prev, ...parsed}));
            } catch (e) {
              console.error('Failed to parse localStorage data:', e);
            }
          }
        }
      })
      .catch(err => {
        console.error('Error loading form data:', err);
        // Fallback to localStorage on API error
        const savedData = localStorage.getItem('investorRelationsDraft');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setFormData(prev => ({...prev, ...parsed}));
          } catch (e) {
            console.error('Failed to parse localStorage data:', e);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    // Load active deals for import dropdown
    try {
      const activeDeals = JSON.parse(localStorage.getItem('activeDeals') || '[]');
      const options = activeDeals.map(deal => ({
        value: String(deal.id),
        label: deal.dealName || 'Unnamed Deal'
      }));
      setDealOptions(options);
    } catch (error) {
      console.error('Failed to load active deals:', error);
    }
  }, []);

  // Auto-save form data to API whenever it changes
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load

    const timeoutId = setTimeout(() => {
      const dataToSave = {
        fullName: formData?.fullName,
        jobTitle: formData?.jobTitle,
        directEmail: formData?.directEmail,
        directPhone: formData?.directPhone,
        linkedinUrl: formData?.linkedinUrl,
        timezone: formData?.timezone,
        weeklyAvailability: formData?.weeklyAvailability
      };

      saveProgress('ir-contact', dataToSave)
        .then(() => {})
        .catch(err => {
          console.error('Failed to auto-save:', err);
          // Fallback to localStorage on API error
          localStorage.setItem('investorRelationsDraft_backup', JSON.stringify(formData));
        });
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [formData, isLoading]);

  const timezoneOptions = [
    { value: 'UTC-12:00', label: '(UTC-12:00) International Date Line West' },
    { value: 'UTC-11:00', label: '(UTC-11:00) Coordinated Universal Time-11' },
    { value: 'UTC-10:00', label: '(UTC-10:00) Hawaii' },
    { value: 'UTC-09:00', label: '(UTC-09:00) Alaska' },
    { value: 'UTC-08:00', label: '(UTC-08:00) Pacific Time (US & Canada)' },
    { value: 'UTC-07:00', label: '(UTC-07:00) Mountain Time (US & Canada)' },
    { value: 'UTC-06:00', label: '(UTC-06:00) Central Time (US & Canada)' },
    { value: 'UTC-05:00', label: '(UTC-05:00) Eastern Time (US & Canada)' },
    { value: 'UTC-04:00', label: '(UTC-04:00) Atlantic Time (Canada)' },
    { value: 'UTC-03:00', label: '(UTC-03:00) Brasilia' },
    { value: 'UTC-02:00', label: '(UTC-02:00) Coordinated Universal Time-02' },
    { value: 'UTC-01:00', label: '(UTC-01:00) Azores' },
    { value: 'UTC+00:00', label: '(UTC+00:00) Greenwich Mean Time' },
    { value: 'UTC+01:00', label: '(UTC+01:00) Central European Time' },
    { value: 'UTC+02:00', label: '(UTC+02:00) Eastern European Time' },
    { value: 'UTC+03:00', label: '(UTC+03:00) Moscow Time' },
    { value: 'UTC+04:00', label: '(UTC+04:00) Gulf Standard Time' },
    { value: 'UTC+05:00', label: '(UTC+05:00) Pakistan Standard Time' },
    { value: 'UTC+05:30', label: '(UTC+05:30) India Standard Time' },
    { value: 'UTC+06:00', label: '(UTC+06:00) Bangladesh Standard Time' },
    { value: 'UTC+07:00', label: '(UTC+07:00) Indochina Time' },
    { value: 'UTC+08:00', label: '(UTC+08:00) China Standard Time' },
    { value: 'UTC+09:00', label: '(UTC+09:00) Japan Standard Time' },
    { value: 'UTC+10:00', label: '(UTC+10:00) Australian Eastern Time' },
    { value: 'UTC+11:00', label: '(UTC+11:00) Solomon Islands Time' },
    { value: 'UTC+12:00', label: '(UTC+12:00) Fiji Time' }
  ];

  const importOptions = [
    { value: '', label: 'Select from previous deals' },
    { value: 'deal-001', label: 'Tech Startup Series A - 2024' },
    { value: 'deal-002', label: 'Healthcare Innovation Round - 2024' },
    { value: 'deal-003', label: 'Green Energy Venture - 2023' }
  ];

  const handleBack = () => {
    navigate('/business-information-form');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      weeklyAvailability: {
        ...prev?.weeklyAvailability,
        [day]: {
          ...prev?.weeklyAvailability?.[day],
          [field]: value
        }
      }
    }));
  };

  const handleCopyToAll = () => {
    const mondaySchedule = formData?.weeklyAvailability?.monday;
    const newSchedule = {};
    
    Object.keys(formData?.weeklyAvailability)?.forEach(day => {
      newSchedule[day] = { ...mondaySchedule };
    });
    
    setFormData(prev => ({
      ...prev,
      weeklyAvailability: newSchedule
    }));
  };

  const handleImportFromDeal = () => {
    if (!selectedDealForImport) return;
    
    try {
      const activeDeals = JSON.parse(localStorage.getItem('activeDeals') || '[]');
      const selectedDeal = activeDeals.find(deal => String(deal.id) === String(selectedDealForImport));
      
      if (selectedDeal && selectedDeal.fullData) {
        const importData = selectedDeal.fullData;
        
        // Import investor relations contact fields
        setFormData(prev => ({
          ...prev,
          fullName: importData.fullName || prev.fullName,
          jobTitle: importData.jobTitle || prev.jobTitle,
          directEmail: importData.directEmail || prev.directEmail,
          directPhone: importData.directPhone || prev.directPhone,
          linkedinUrl: importData.linkedinUrl || prev.linkedinUrl,
          timezone: importData.timezone || prev.timezone,
          weeklyAvailability: importData.weeklyAvailability || prev.weeklyAvailability
        }));
      }
    } catch (error) {
      console.error('Failed to import from deal:', error);
    }
  };

  const handleSaveExit = async () => {
    try {
      const dataToSave = {
        fullName: formData?.fullName,
        jobTitle: formData?.jobTitle,
        directEmail: formData?.directEmail,
        directPhone: formData?.directPhone,
        linkedinUrl: formData?.linkedinUrl,
        timezone: formData?.timezone,
        weeklyAvailability: formData?.weeklyAvailability,
        createdAt: new Date().toISOString()
      };

      await saveProgress('ir-contact', dataToSave);
      navigate('/deals-dashboard-empty-state');
    } catch (error) {
      console.error('Failed to save form data:', error);
      alert('Failed to save form data. Please check your connection and try again.');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      const dataToSave = {
        fullName: formData?.fullName,
        jobTitle: formData?.jobTitle,
        directEmail: formData?.directEmail,
        directPhone: formData?.directPhone,
        linkedinUrl: formData?.linkedinUrl,
        timezone: formData?.timezone,
        weeklyAvailability: formData?.weeklyAvailability,
        createdAt: new Date().toISOString()
      };

      await saveProgress('ir-contact', dataToSave);
      navigate('/calendar-booking');
    } catch (error) {
      console.error('Failed to save form data:', error);
      alert('Failed to save form data. Please check your connection and try again.');
    }
  };

  const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <FormContainer maxWidth="max-w-2xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Investor Relations Contact</h1>
            <p className="text-gray-600 mb-2">Tell us about the person who will handle investor calls and communications</p>
            <p className="text-sm text-gray-500">This information will be used to personalize communications and enable the AI assistant to schedule calls appropriately</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Import from Previous Deal */}
          {dealOptions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Import from Previous Deal</h3>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Select
                    options={[{ value: '', label: 'Select a deal...' }, ...dealOptions]}
                    value={selectedDealForImport}
                    onChange={setSelectedDealForImport}
                    placeholder="Select a deal..."
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleImportFromDeal}
                  disabled={!selectedDealForImport}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Import
                </Button>
              </div>
            </div>
          )}

          {/* Contact Information Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <Input
                type="text"
                value={formData?.fullName}
                onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                placeholder="Enter full name"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Job Title</label>
              <Input
                type="text"
                value={formData?.jobTitle}
                onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
                placeholder="e.g., Investor Relations Manager"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Direct Email</label>
              <Input
                type="email"
                value={formData?.directEmail}
                onChange={(e) => handleInputChange('directEmail', e?.target?.value)}
                placeholder="name@company.com"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Direct Phone Number</label>
              <Input
                type="tel"
                value={formData?.directPhone}
                onChange={(e) => handleInputChange('directPhone', e?.target?.value)}
                placeholder="+1 (555) 000-0000"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">LinkedIn URL</label>
              <Input
                type="url"
                value={formData?.linkedinUrl}
                onChange={(e) => handleInputChange('linkedinUrl', e?.target?.value)}
                placeholder="https://linkedin.com/in/yourprofile"
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Timezone</label>
              <Select
                value={formData?.timezone}
                onChange={(value) => handleInputChange('timezone', value)}
                options={timezoneOptions}
                placeholder="Select timezone"
                className="w-full"
                searchable
                required
              />
            </div>
          </div>

          {/* Weekly Availability Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Weekly Availability</h2>
              <button
                type="button"
                onClick={handleCopyToAll}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                Copy to all
              </button>
            </div>
            <p className="text-sm text-gray-600">Set your regular hours of availability for each day of the week</p>
            
            <div className="space-y-3">
              {dayNames?.map((day, index) => (
                <AvailabilityToggle
                  key={day}
                  day={day}
                  label={dayLabels?.[index]}
                  availability={formData?.weeklyAvailability?.[day]}
                  onChange={(field, value) => handleAvailabilityChange(day, field, value)}
                />
              ))}
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="pt-6">
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              size="lg"
            >
              Continue
            </Button>
          </div>
        </form>
      </div>
    </FormContainer>
  );
};

export default InvestorRelationsContact;